import { useEffect } from 'react'
import { useParams } from 'react-router-dom'

import useAppSelector from '../useAppSelector'
import { useAlternativeProducts } from './useAlternativeProducts'

/**
 * PDP-scoped trigger for the "Opciones similares" (alternativeProducts) carousel.
 *
 * The carousel is SKU-keyed — it must fetch alternatives for the product shown on
 * the product-detail page. It CANNOT be driven by a `runOnLoad` template binding:
 * `{{params.sku}}` is a source shortcut for `state.defaultSlice.params.sku`, but
 * `RouterParams` (mounted at the app root) populates that in an effect that runs
 * *after* a deep carousel's one-shot `runOnLoad` fires — so at fire time the value
 * is empty and the binding is never retried. (The same `{{params.sku}}` resolves
 * fine in the counter's cart actions, because those resolve at click time, once
 * `params` is populated.)
 *
 * So we trigger the fetch here instead, reactively, from the mounted PDP template:
 * read the sku from the route (`useParams`) and (re-)fetch whenever it changes. On
 * an sku change the underlying fetch drops the previous product's items before
 * loading; a missing sku / not-yet-bootstrapped identifiers simply no-op, and the
 * effect re-runs once the identifiers arrive.
 */
export const useAlternativeProductsPdpTrigger = () => {
  const { sku } = useParams()
  const storeName = useAppSelector((state) => state.defaultSlice.storeName)
  const sessionId = useAppSelector((state) => state.defaultSlice.sessionId)
  const fetchAlternativeProducts = useAlternativeProducts()

  useEffect(() => {
    // On the mounted PDP the route guarantees `sku`; `storeName`/`sessionId` can
    // be briefly empty before session bootstrap, so this is a "not ready" no-op
    // that the dep array re-runs once they populate. Kept silent deliberately: an
    // absent sku here would be a routing bug, not a data condition — and the old
    // runOnLoad path's "missing skus" Datadog log fired on every PDP load because
    // of the race, i.e. it was noise, not signal.
    if (!sku || !storeName || !sessionId) return
    fetchAlternativeProducts({
      storefrontName: storeName,
      sessionUid: sessionId,
      skus: [sku],
    })
    // `fetchAlternativeProducts` is re-created every render; fire only when the
    // displayed sku (or the store identifiers) change, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sku, storeName, sessionId])
}
